import { useMemo } from 'react';
import type { TaskEvent } from '@shared/types/tasks';
import { formatDateTime } from '@/lib/formatters';
import { FactorySubtasksCard } from './FactorySubtasksCard';
import { proposedSubtasksAnchorId, subtaskProposalId, subtaskProposals, type SubtaskProposal } from './task-subtask-proposals';
import { formatRelative } from './task-ui';
import { payloadConcerns } from '@shared/decision-concern';

/**
 * The sub-tasks the AI builder proposed on a task, beside its Decisions and Suggestions, newest run
 * first. SF-T-178: only the decided ones (added, partly added or rejected), each folded to one line that
 * opens on a click; the ones still to decide are at the top of the task (TaskToAnswer). The comments
 * show a pointer line (FactorySubtasksPointer) in place of each run's block. Nothing shows without a
 * decided proposal. On a sub-task (`all`), which can't have sub-tasks, nothing waits at the top: every
 * proposal is listed here.
 */
export function ProposedSubtasks({
  taskId,
  events,
  all = false,
}: {
  taskId: string;
  /** The task's events, oldest first. */
  events: TaskEvent[];
  /** Undecided proposals too (a sub-task's, which only list). */
  all?: boolean;
}) {
  const proposals = useMemo(() => subtaskProposals(events).filter((p) => all || p.decided), [events, all]);
  if (!proposals.length) return null;

  return (
    <section id={proposedSubtasksAnchorId(taskId)} className="scroll-mt-4 space-y-2" aria-label="Proposed sub-tasks">
      <h3 className="text-sm font-semibold">Proposed sub-tasks</h3>
      <div className="space-y-2">
        {proposals.map((proposal) => (
          <SubtaskProposalCard key={proposal.event.id} proposal={proposal} />
        ))}
      </div>
    </section>
  );
}

/** One run's proposal: folded once decided, open to add or reject while it isn't (with `onAdd`). */
export function SubtaskProposalCard({
  proposal,
  onAdd,
}: {
  proposal: SubtaskProposal;
  /** Adds the ticked sub-tasks (addProposedSubtasks; none rejects them all); resolves true when done. Leave out in a preview or on a sub-task. */
  onAdd?: (eventId: string, numbers: number[]) => Promise<boolean>;
}) {
  return (
    <FactorySubtasksCard
      id={subtaskProposalId(proposal.event.id)}
      fold={Boolean(proposal.decided)}
      subtasks={proposal.subtasks}
      concerns={payloadConcerns(proposal.event.payload)}
      decided={proposal.decided}
      onAdd={onAdd ? (numbers) => onAdd(proposal.event.id, numbers) : undefined}
      meta={
        <span data-hint={formatDateTime(new Date(proposal.event.created_at))}>
          {proposal.event.actor_label ? `${proposal.event.actor_label} · ` : null}
          {formatRelative(proposal.event.created_at)}
        </span>
      }
    />
  );
}
